import React from "react";
import { StyleSheet, View } from "react-native";
import {
  useNavigation,
  type NavigationProp,
  type ParamListBase,
} from "@react-navigation/native";
import { ChevronRight, Pill } from "lucide-react-native";

import { DocumentRow } from "@louise/moon-ui";
import { EMBER, GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import { useTranslations } from "@src/packages/i18n";

import { useBasicInfo } from "../BasicInfoContext";
import { BasicInfoQuestionLayout } from "../components";
import { navigateToMedicationEditor } from "../medicationSummaryNavigation";

export function MedicationsScreen() {
  const t = useTranslations();
  const { state, next } = useBasicInfo("Medications");
  const navigation = useNavigation<NavigationProp<ParamListBase>>();
  const medications = (state.medications ?? []).filter(
    (medication) => medication.op !== "delete" && medication.name.trim(),
  );

  return (
    <BasicInfoQuestionLayout
      route="Medications"
      title={t("med_history.basic_info.medications.current_medications")}
      subtitle={t(
        "med_history.basic_info.medications.edit_medications_in_medications_and_vaccines",
      )}
      helpText={t("med_history.basic_info.help.medications")}
      onBack={() => navigation.goBack()}
      onSave={() => next()}
      saveLabel={t("med_history.shared.next")}
    >
      <View style={styles.container}>
        <View style={[GLASS_CARD_STYLE, styles.summaryCard]}>
          {medications.length > 0 ? (
            medications.map((medication, index) => (
              <View
                key={medication.id ?? `${medication.name}-${index}`}
                style={[
                  styles.medication,
                  index > 0 ? styles.medicationDivider : null,
                ]}
              >
                <Pill size={18} color={EMBER.colors.coral} />
                <View style={styles.medicationText}>
                  <Text style={styles.medicationName}>{medication.name}</Text>
                  {medication.brand || medication.frequency ? (
                    <Text style={styles.medicationDetail}>
                      {[medication.brand, medication.frequency]
                        .filter(Boolean)
                        .join(" · ")}
                    </Text>
                  ) : null}
                </View>
              </View>
            ))
          ) : (
            <Text style={styles.emptyText}>
              {t(
                "med_history.basic_info.medications.no_current_medications_recorded",
              )}
            </Text>
          )}
        </View>

        <DocumentRow
          accessibilityLabel={t(
            "med_history.basic_info.medications.manage_medications_and_vaccines",
          )}
          leading={<Pill size={18} color={EMBER.colors.coral} />}
          onPress={() => navigateToMedicationEditor(navigation)}
          title={t(
            "med_history.basic_info.medications.manage_medications_and_vaccines",
          )}
          subtitle={t(
            "med_history.basic_info.medications.open_categorized_medical_history_editor",
          )}
          trailing={
            <ChevronRight
              size={18}
              color={EMBER.colors.textLight}
              strokeWidth={1.5}
            />
          }
          showChevron={false}
        />
      </View>
    </BasicInfoQuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 16,
    width: "100%",
  },
  summaryCard: {
    padding: 16,
  },
  medication: {
    alignItems: "center",
    flexDirection: "row",
    gap: 12,
  },
  medicationDivider: {
    borderTopColor: "rgba(84, 47, 39, 0.10)",
    borderTopWidth: StyleSheet.hairlineWidth,
    marginTop: 12,
    paddingTop: 12,
  },
  medicationText: {
    flex: 1,
    gap: 2,
  },
  medicationName: {
    color: EMBER.colors.text,
    fontSize: 15,
    fontWeight: "600",
  },
  medicationDetail: {
    color: EMBER.colors.textLight,
    fontSize: 13,
  },
  emptyText: {
    color: EMBER.colors.textLight,
    fontSize: 14,
    lineHeight: 20,
    textAlign: "center",
  },
});

export default MedicationsScreen;
